import { useEffect, useId, useLayoutEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react'
import { createPortal } from 'react-dom'

import {
  MONTH_NAMES,
  WEEKDAY_LABELS,
  addMonths,
  boundsFooter,
  clampIso,
  displayToIso,
  isAllowed,
  isoToDisplay,
  monthGrid,
  moveFocus,
  nearestAllowedMonth,
  normalizeTyping,
  parseIso,
} from '@/ui/lib/dateField'
import { cn } from '@/ui/lib/utils'

export interface DateFieldProps {
  /** The held value: an ISO `YYYY-MM-DD`, or the raw text of an invalid entry. */
  value: string
  /** A valid date arrives as ISO; an invalid entry (on blur or Enter) arrives as the raw typed text. */
  onChange: (next: string) => void
  /** Inclusive ISO bounds; `null` or omitted is open on that side. */
  min?: string | null
  max?: string | null
  /** The param name; the text input's accessible name. */
  ariaLabel: string
  invalid?: boolean
  disabled?: boolean
  className?: string
}

const CAL_W = 252

function todayIso(): string {
  const d = new Date()
  return `${String(d.getFullYear()).padStart(4, '0')}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

function fullLabel(iso: string): string {
  const p = parseIso(iso)
  if (p === null) return iso
  return `${p.day} ${MONTH_NAMES[p.month - 1]} ${p.year}`
}

/**
 * A DD.MM.YYYY text field with a Monday-first calendar popover (spec 073 B1).
 * The value in and out is ISO; the typed text is shown in DD.MM.YYYY whatever
 * the browser locale. Days outside `min`/`max` are disabled in the grid, typed
 * out-of-range dates are the caller's to warn about. Fixed size: the popover is
 * portaled and never moves the row.
 */
export function DateField({ value, onChange, min = null, max = null, ariaLabel, invalid, disabled, className }: DateFieldProps) {
  const [draft, setDraft] = useState(() => isoToDisplay(value) ?? value)
  const lastEmitted = useRef(value)
  const [open, setOpen] = useState(false)
  const [focusIso, setFocusIso] = useState('')
  const [view, setView] = useState({ year: 2026, month: 1 })
  const [pos, setPos] = useState<{ top?: number; bottom?: number; left: number } | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const dialogRef = useRef<HTMLDivElement>(null)
  const dialogId = useId()

  // An external change (Use preview range, a loaded config) replaces the typed text.
  useEffect(() => {
    if (value === lastEmitted.current) return
    lastEmitted.current = value
    setDraft(isoToDisplay(value) ?? value)
  }, [value])

  function emit(next: string) {
    lastEmitted.current = next
    onChange(next)
  }

  function commitDraft() {
    const iso = displayToIso(draft)
    if (iso !== null) {
      setDraft(isoToDisplay(iso) ?? draft)
      if (iso !== value) emit(iso)
    } else if (draft !== (isoToDisplay(value) ?? value)) {
      emit(draft)
    }
  }

  function openCalendar() {
    const start = parseIso(value) !== null ? value : max !== null ? max : todayIso()
    const focus = clampIso(start, min, max)
    const p = parseIso(focus) ?? parseIso(todayIso())
    if (p === null) return
    setFocusIso(focus)
    setView({ year: p.year, month: p.month })
    setOpen(true)
  }

  function close(returnFocus: boolean) {
    setOpen(false)
    if (returnFocus) buttonRef.current?.focus()
  }

  // Position against the input; keep it on screen (flip above when low on the page).
  useLayoutEffect(() => {
    if (!open) return
    function update() {
      const rect = inputRef.current?.getBoundingClientRect()
      if (!rect) return
      const left = Math.max(8, Math.min(rect.left, window.innerWidth - CAL_W - 8))
      const below = window.innerHeight - rect.bottom
      if (below < 330 && rect.top > below) setPos({ bottom: window.innerHeight - rect.top + 4, left })
      else setPos({ top: rect.bottom + 4, left })
    }
    update()
    window.addEventListener('scroll', update, true)
    window.addEventListener('resize', update)
    return () => {
      window.removeEventListener('scroll', update, true)
      window.removeEventListener('resize', update)
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    function onPointerDown(event: PointerEvent) {
      const target = event.target as Node
      if (dialogRef.current?.contains(target) || buttonRef.current?.contains(target) || inputRef.current?.contains(target)) return
      setOpen(false)
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key !== 'Escape') return
      event.preventDefault()
      event.stopPropagation()
      close(true)
    }
    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown, true)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown, true)
    }
  }, [open])

  // The roving-tabindex day takes focus when the popover opens and when a key moves it.
  useEffect(() => {
    if (!open || pos === null) return
    dialogRef.current?.querySelector<HTMLButtonElement>('button[data-day][tabindex="0"]')?.focus()
  }, [open, pos, focusIso])

  function select(iso: string) {
    if (!isAllowed(iso, min, max)) return
    setDraft(isoToDisplay(iso) ?? '')
    emit(iso)
    close(true)
  }

  function onDayKey(event: ReactKeyboardEvent<HTMLButtonElement>) {
    const next = moveFocus(focusIso, event.key, event.shiftKey, min, max)
    if (next === null) return
    event.preventDefault()
    const p = parseIso(next)
    if (p === null) return
    setFocusIso(next)
    setView({ year: p.year, month: p.month })
  }

  function shiftView(months: number) {
    const base = `${String(view.year).padStart(4, '0')}-${String(view.month).padStart(2, '0')}-01`
    const next = addMonths(base, months)
    const p = parseIso(next)
    if (p === null) return
    setView({ year: p.year, month: p.month })
    setFocusIso(clampIso(`${next.slice(0, 7)}-${String(Math.min(parseIso(focusIso)?.day ?? 1, 28)).padStart(2, '0')}`, min, max))
  }

  const jump = open ? nearestAllowedMonth(view.year, view.month, min, max) : null
  const footer = boundsFooter(min, max)
  const today = todayIso()
  const navBtn = 'h-6 w-6 border border-ink-25 bg-transparent font-mono text-[12px] leading-none text-ink hover:bg-ink-14'

  return (
    <div className={cn('flex min-w-0 items-stretch gap-1', className)}>
      <input
        ref={inputRef}
        type="text"
        inputMode="numeric"
        placeholder="DD.MM.YYYY"
        autoComplete="off"
        maxLength={10}
        aria-label={ariaLabel}
        aria-invalid={invalid ? true : undefined}
        disabled={disabled}
        value={draft}
        onChange={(event) => {
          const next = normalizeTyping(event.target.value, draft)
          setDraft(next)
          const iso = displayToIso(next)
          if (iso !== null) emit(iso)
        }}
        onBlur={commitDraft}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault()
            commitDraft()
          }
        }}
        className={cn(
          'min-w-0 flex-1 border bg-transparent px-[9px] py-[7px] font-mono text-[12.5px] text-ink outline-none focus:border-accent-fill',
          invalid ? 'border-alert' : 'border-ink-25',
        )}
      />
      <button
        ref={buttonRef}
        type="button"
        aria-label="Open calendar"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? dialogId : undefined}
        disabled={disabled}
        onClick={() => (open ? close(false) : openCalendar())}
        className="w-8 shrink-0 border border-ink-25 bg-transparent font-mono text-[11px] text-ink-50 hover:text-ink"
      >
        ▦
      </button>
      {open &&
        pos !== null &&
        createPortal(
          <div
            ref={dialogRef}
            id={dialogId}
            role="dialog"
            aria-label="Choose a date"
            style={{ position: 'fixed', top: pos.top, bottom: pos.bottom, left: pos.left, width: CAL_W }}
            className="z-[1000] border border-ink-25 bg-paper p-2 font-mono text-[11.5px] text-ink shadow-lg"
          >
            <div className="flex h-7 items-center justify-between gap-1">
              <button type="button" aria-label="Previous year" onClick={() => shiftView(-12)} className={navBtn}>«</button>
              <button type="button" aria-label="Previous month" onClick={() => shiftView(-1)} className={navBtn}>‹</button>
              <div aria-live="polite" className="min-w-0 flex-1 truncate text-center font-semibold">
                {MONTH_NAMES[view.month - 1]} {view.year}
              </div>
              <button type="button" aria-label="Next month" onClick={() => shiftView(1)} className={navBtn}>›</button>
              <button type="button" aria-label="Next year" onClick={() => shiftView(12)} className={navBtn}>»</button>
            </div>
            <table role="grid" aria-label={`${MONTH_NAMES[view.month - 1]} ${view.year}`} className="mt-1 w-full table-fixed border-collapse">
              <thead>
                <tr>
                  {WEEKDAY_LABELS.map((label) => (
                    <th key={label} scope="col" className="h-6 text-[10px] font-normal text-ink-50">
                      {label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {monthGrid(view.year, view.month).map((week) => (
                  <tr key={week[0].iso}>
                    {week.map((cell) => {
                      const allowed = isAllowed(cell.iso, min, max)
                      const selected = cell.iso === value
                      return (
                        <td key={cell.iso} role="gridcell" aria-selected={selected} className="p-0 text-center">
                          <button
                            type="button"
                            data-day={cell.iso}
                            data-today={cell.iso === today ? '' : undefined}
                            aria-label={fullLabel(cell.iso)}
                            aria-disabled={allowed ? undefined : true}
                            tabIndex={cell.iso === focusIso ? 0 : -1}
                            onClick={() => select(cell.iso)}
                            onKeyDown={onDayKey}
                            className={cn(
                              'h-7 w-full border bg-transparent text-[11.5px]',
                              cell.inMonth ? 'text-ink' : 'text-ink-50',
                              !allowed && 'cursor-not-allowed opacity-35 line-through',
                              selected ? 'border-ink bg-ink text-paper' : cell.iso === today ? 'border-accent-fill' : 'border-transparent',
                            )}
                          >
                            {cell.day}
                          </button>
                        </td>
                      )
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="mt-1 flex h-6 items-center justify-between gap-2 text-[10.5px] text-ink-50">
              <span className="min-w-0 truncate" title={footer ?? undefined}>{footer ?? ''}</span>
              {jump !== null && (
                <button
                  type="button"
                  onClick={() => {
                    const p = parseIso(jump)
                    if (p === null) return
                    setView({ year: p.year, month: p.month })
                    setFocusIso(clampIso(jump, min, max))
                  }}
                  className="shrink-0 border border-ink-25 px-1.5 text-ink"
                >
                  Go to {MONTH_NAMES[(parseIso(jump)?.month ?? 1) - 1]} {parseIso(jump)?.year}
                </button>
              )}
            </div>
          </div>,
          document.body,
        )}
    </div>
  )
}
